import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { User } from '@hirebridge/types'
import { api, primeCsrf, setActiveCompany } from './api'

interface AuthState {
  user: User | null
  loading: boolean
  isLoginModalOpen: boolean
  modalRedirectPath: string | null
  openLoginModal: (redirectPath?: string) => void
  closeLoginModal: () => void
  login: (email: string, password: string) => Promise<User>
  loginWithGoogle: (payload: {
    credential?: string
    token?: string
    email?: string
    first_name?: string
    last_name?: string
    avatar_url?: string
    role?: 'candidate' | 'employer' | 'freelancer'
    company_name?: string
  }) => Promise<User>
  logout: () => Promise<void>
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false)
  const [modalRedirectPath, setModalRedirectPath] = useState<string | null>(null)

  const openLoginModal = (redirectPath?: string) => {
    setModalRedirectPath(redirectPath ?? null)
    setIsLoginModalOpen(true)
  }

  const closeLoginModal = () => {
    setIsLoginModalOpen(false)
    setModalRedirectPath(null)
  }

  const refresh = async () => {
    try {
      const { user } = await api.me()
      setUser(user)
      if (user.companies.length > 0) setActiveCompany(user.companies[0]!.id)
    } catch {
      // A 401 here is the normal signed-out case, not an error worth showing.
      setUser(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void refresh()
  }, [])

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      isLoginModalOpen,
      modalRedirectPath,
      openLoginModal,
      closeLoginModal,
      login: async (email, password) => {
        await primeCsrf()
        const { user } = await api.login(email, password)
        setUser(user)
        if (user.companies.length > 0) setActiveCompany(user.companies[0]!.id)
        return user
      },
      loginWithGoogle: async (payload) => {
        await primeCsrf()
        const { user } = await api.oauthGoogle(payload)
        setUser(user)
        if (user.companies.length > 0) setActiveCompany(user.companies[0]!.id)
        return user
      },
      logout: async () => {
        await api.logout()
        setUser(null)
        setActiveCompany(null)
      },
      refresh,
    }),
    [user, loading, isLoginModalOpen, modalRedirectPath],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside AuthProvider')
  return context
}